import { useCallback, useMemo } from 'react';

import { useWorkspaceOrganisations } from '@futrli/organisations';

import { convertToSubEntityForecastParams } from './utils';
import { FixedSubEntityImportSheetInfo, LinkedSheetData } from '../../import/types';
import { useConsolidatedForecastContext } from '../../../../context/consolidated-forecast-context';
import { useConsolidationDataContext } from '../../../../../../predict-management/context/consolidation-data-context';
import { useGoogleSheetsToken } from '../use-google-sheets-token/use-google-sheets-token';
import { useImportSheetsInfo } from '../use-import-sheets-info/use-import-sheets-info';

export const useLinkedSheetData = (redirectURL: string): { data: LinkedSheetData[]; refreshSheetsInfo: () => void } => {
  const { forecast } = useConsolidatedForecastContext();
  const { consolidation, workspaceId } = useConsolidationDataContext();
  const { organisations } = useWorkspaceOrganisations(workspaceId);

  const sheetInfo = forecast?.sub_entity_import_sheet_info as FixedSubEntityImportSheetInfo | undefined;

  const subEntityForecastParams = useMemo(() => {
    return convertToSubEntityForecastParams(workspaceId, sheetInfo);
  }, [sheetInfo, workspaceId]);

  const { isLoading: tokenLoading, result: tokenResult, refresh: refreshToken } = useGoogleSheetsToken(redirectURL);
  const { idsLoading: sheetInfoIdsLoading, results: sheetInfoResults, refresh: refreshSheets } = useImportSheetsInfo(
    subEntityForecastParams
  );

  const refreshSheetsInfo = useCallback(() => {
    refreshToken();
    refreshSheets();
  }, [refreshSheets, refreshToken]);

  if (!sheetInfo) {
    return { data: [], refreshSheetsInfo };
  }

  const data = subEntityForecastParams.map(params => ({
    ...params,
    entity: organisations?.find(org => org.id === params.organisationId),
    lastSyncedAt: sheetInfo[params.organisationId].import_sheet_last_sync_at || undefined,
    sheetInfo: sheetInfoResults[params.forecastId],
    sheetInfoLoading: sheetInfoIdsLoading.includes(params.forecastId),
    shortCode: consolidation?.organisations.find(org => org.organisation_id === params.organisationId)?.short_code,
    token: tokenResult,
    tokenLoading
  }));

  return { data, refreshSheetsInfo };
};
